import { showToastMessage } from "@almafintech/react-components";
import * as Sentry from "@sentry/react";
import { FormikValues } from "formik";
import {
  getActiveQuestionnaire,
  getAvailableRiskProfiles,
  registerWithDocuments,
  updatePersonalInformation,
} from "../../connectors/interactiveBrokers";
import {
  labels,
  placeholders,
  title,
} from "../../i18n/interactiveBrokers/personalInformation";
import { onUpdateInteractiveBrokersData } from "../../reducers/interactiveBrokersReducer";
import { onUpdateCompletedStep } from "../../reducers/onboardingDataReducer";
import { Option, Step } from "../../types";
import { genericToastifyError } from "../../utils";
import { validationsForLanguage } from "../../validations";

const personalInformationStep = ({
  language = "es",
  countriesOptions,
  referralOptions,
}: {
  language: "es" | "en";
  countriesOptions: Option[];
  referralOptions: Option[];
}): Step => {
  const validations = validationsForLanguage(language);

  return {
    step: 2,
    name: title[language],
    titleOfStep: title[language],
    fields: [
      {
        initialValue: "",
        type: "text",
        name: "firstName",
        label: labels[language].firstName,
        validation: validations.stringRequired,
      },
      {
        initialValue: "",
        type: "text",
        name: "lastName",
        label: labels[language].lastName,
        validation: validations.stringRequired,
      },
      {
        name: "countryOfBirth",
        type: "select",
        label: labels[language].countryOfBirth,
        placeholder: placeholders[language].countryOfBirth,
        validation: validations.radioOrSingleSelectRequired(countriesOptions),
        options: countriesOptions,
        selectType: "single",
        initialValue: "",
      },
      {
        type: "select",
        name: "referral",
        label: labels[language].referral,
        validation: referralOptions.length
          ? validations.radioOrSingleSelectRequired(referralOptions)
          : undefined,
        options: referralOptions,
        selectType: "single",
        initialValue: "",
      },
    ],
    onContinue: async (values: FormikValues, dispatch, state) => {
      try {
        if (!state || !state.validations || !dispatch)
          throw new Error("Error: state or dispatch not found");

        const body = {
          email: values.email,
          first_name: values.firstName,
          last_name: values.lastName,
          country_of_residence: values.countryOfBirth,
          language_preference: language,
          referral: values.referral
            ? Number.parseInt(values.referral)
            : undefined,
        };

        const response = await updatePersonalInformation(body)
          .then()
          .catch(async ({ code }) =>
            code === "ERR_BAD_REQUEST"
              ? await registerWithDocuments(body)
              : Promise.reject()
          );

        // Update personal information stored on redux slice
        const personalInformation = {
          id: response.data.user.id,
          email: response.data.user.email,
          firstName: response.data.user.first_name,
          lastName: response.data.user.last_name,
          countryOfBirth: response.data.user.country_of_residence,
          referral: {
            id: response.data.user.referral,
            name:
              referralOptions.find(
                ({ value }) => value === response.data.user.referral.toString()
              )?.label ?? "",
          },
        };
        dispatch(onUpdateInteractiveBrokersData({ personalInformation }));

        // Update pending documents stored on redux slice
        const documentsToAccept = response.data.documents_to_accept.map(
          (responseDocument) => ({
            ...responseDocument,
            createdAt: new Date(responseDocument.created_at),
          })
        );

        const BASE_URL = import.meta.env.VITE_BASE_URL;
        dispatch(
          onUpdateInteractiveBrokersData({
            documentsToAccept: documentsToAccept.map(
              ({
                id,
                version,
                document_name,
                created_at,
                pdf_url,
                html_content,
              }) => ({
                id,
                version,
                name: document_name,
                createdAt: new Date(created_at),
                url: `${BASE_URL}${pdf_url}`,
                encodedHtml: html_content,
              })
            ),
          })
        );

        // Fetch the questionnaire used on the following step
        const {
          data: { id: questionnaireId, questions },
        } = await getActiveQuestionnaire();
        const riskProfileQuestionnaire = {
          id: questionnaireId,
          questions: questions
            .sort((question) => question.order)
            .map(({ id, statement, possible_answers }) => ({
              id,
              statement,
              options: possible_answers.map(({ id, text }) => ({
                label: text,
                value: id.toString(),
              })),
            })),
        };
        dispatch(
          onUpdateInteractiveBrokersData({
            riskProfileQuestionnaire,
          })
        );

        // Fetch available risk profiles used on the last substep of the following step
        const { data: availableRiskProfilesResponse } =
          await getAvailableRiskProfiles();
        const availableRiskProfiles = availableRiskProfilesResponse.map(
          ({
            name,
            max_equity_exposure,
            target_equity_exposure,
            target_fixed_income_exposure,
          }) => ({
            name,
            maxEquity: max_equity_exposure,
            equity: target_equity_exposure,
            fixedIncome: target_fixed_income_exposure,
          })
        );
        dispatch(
          onUpdateInteractiveBrokersData({
            availableRiskProfiles,
          })
        );

        dispatch(onUpdateCompletedStep(2));
      } catch (error) {
        console.error("Error on step two:", error);
        Sentry.captureException(error);
        showToastMessage(genericToastifyError, {
          containerId: "formAlert",
          type: "error",
        });
      }
    },
  };
};

export default personalInformationStep;
